import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { SohoContextMenuDirective } from 'ids-enterprise-ng';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector:    '[lm-field-options]',
  templateUrl: './lm-field-options.component.html',
  styleUrls:   [ 'lm-field-options.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMFieldOptionsComponent implements OnDestroy {

  @ViewChild(SohoContextMenuDirective) contextmenu: SohoContextMenuDirective;
  @ViewChild('FieldOptionsButton', {read: ElementRef}) fieldOptionsButton: ElementRef<HTMLElement>;

  @Input() ajaxCallbackFunction: SohoPopupMenuSourceFunction;
  @Input() contextMenuEventFunction: Function;
  @Input() hasContextMenu: boolean;
  @Input() lmParagraphLayoutInfo: LMParagraphLayoutInfo;
  @Input() dataEntryMode = false;
  @Input() isMemberField = false;
  @Input() isCheckBoxField = true;
  @Input() buttonId: string;
  @Input() fieldAutomationId: string;
  @Input() fieldOptionsLabel: string;
  @Input() isInAccessibilityMode: boolean;
  public showFieldOptions = false;
  @Input() displayFieldOptions = false;

  private fieldOptionsClicked = false;
  public overrideTabbability = false;

  @HostBinding('style.position') get hostPosition() {
    return 'relative';
  }

  @HostBinding('style.display') get hostDisplay() {
    // If a single textarea in a paragraph layout
    // Allow textarea to display block so it will span 100% width
    if ((this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.textAreaOnly) || this.isCheckBoxField) {
      return '';
    } else {
      return 'inline-flex';
    }
  }

  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private zone: NgZone) {
    const element = el.nativeElement;
    const mouseEnter$ = fromEvent(element, 'mouseenter', { passive: true });
    const mouseLeave$ = fromEvent(element, 'mouseleave', { passive: true });

    mouseEnter$.pipe(
      tap(() => {
        this.showFieldOptions = true;
        this.changeDetectorRef.markForCheck();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    mouseLeave$.pipe(
      tap(() => {
        if (this.fieldOptionsClicked) {
          // if the field options has been clicked, leave the button visible for a bit
          this.zone.runOutsideAngular(() => {
            setTimeout(() => {
              this.showFieldOptions = false;
              this.fieldOptionsClicked = false;
              this.changeDetectorRef.markForCheck();
            }, 100);
          });
        } else {
          this.showFieldOptions = false;
          this.changeDetectorRef.markForCheck();
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  public ngOnDestroy() {
    this.destroy$.next();
  }
  handleFocus() {
    this.fieldOptionsButton?.nativeElement?.classList.remove('hide-focus');
  }

  public openFieldOptions(event: Event) {
    if (event) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
    }
    this.fieldOptionsClicked = true;
    this.showFieldOptions = true;
    this.contextmenu.initializeComponent();
  }

  public onFieldOptionsContextMenuClose() {
    this.showFieldOptions = true;
    this.fieldOptionsClicked = false;
    // LMCLIENT-38107 on modals, we need to make the button tabbable to not mess up the tab order.
    if (!this.isInAccessibilityMode) {
      this.overrideTabbability = true;
    }
    if (this.fieldOptionsButton?.nativeElement) {
      this.fieldOptionsButton.nativeElement.focus();
    }
  }

  handleBlur() {
    if (!this.isInAccessibilityMode) {
      this.overrideTabbability = false;
    }
    if (!this.fieldOptionsClicked) {
      this.showFieldOptions = false;
    }
  }
}
